import React, { useState } from 'react';
import { Box, Typography, Button, Chip } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckIcon from '@mui/icons-material/Check';
import { post } from '../../utils/apiClient';
import { logger } from '../../utils/logger';

interface Suggestion {
  session_type: string;
  template_id: string;
  template_name: string;
  percentage: number;
  usage_count: number;
  message: string;
}

interface TemplateSuggestionProps {
  suggestions: Suggestion[];
}

/**
 * Smart template suggestion component.
 * Shows when the system detects the therapist consistently uses a
 * specific template for a session type (>= 70% usage, >= 5 uses).
 * Offers to set it as their default.
 */
function TemplateSuggestion({ suggestions }: TemplateSuggestionProps) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [saved, setSaved] = useState<Set<string>>(new Set());

  if (!suggestions || suggestions.length === 0) {
    return null;
  }

  const visibleSuggestions = suggestions.filter(
    (s) => !dismissed.has(s.session_type) && !saved.has(s.session_type)
  );

  if (visibleSuggestions.length === 0) {
    return null;
  }

  const handleSetDefault = async (suggestion: Suggestion) => {
    try {
      const response = await post(
        `/api/therapist_dashboard/briefing/template-default?session_type=${encodeURIComponent(suggestion.session_type)}&template_id=${encodeURIComponent(suggestion.template_id)}`
      );

      if (response.ok) {
        setSaved((prev) => new Set([...prev, suggestion.session_type]));
      }
    } catch (err) {
      logger.warn('Failed to set template default:', err);
    }
  };

  const handleDismiss = (sessionType: string) => {
    setDismissed((prev) => new Set([...prev, sessionType]));
  };

  return (
    <Box sx={{ mt: 1 }}>
      {visibleSuggestions.map((suggestion) => (
        <Box
          key={suggestion.session_type}
          sx={{
            p: 1.5,
            borderRadius: 1,
            bgcolor: 'info.light',
            border: 1,
            borderColor: 'info.main',
            mb: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <AutoAwesomeIcon
              sx={{ fontSize: 18, color: 'info.main', mt: 0.25 }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2">
                {suggestion.message}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  startIcon={<CheckIcon />}
                  onClick={() => handleSetDefault(suggestion)}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Set as default
                </Button>
                <Button
                  size="small"
                  variant="text"
                  color="inherit"
                  onClick={() => handleDismiss(suggestion.session_type)}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Not now
                </Button>
                <Chip
                  label={`${suggestion.usage_count} uses`}
                  size="small"
                  variant="outlined"
                  sx={{ ml: 'auto', fontSize: '0.7rem' }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export default TemplateSuggestion;
